import { Dom, gsap } from '@brandocms/jupiter'

export default app => ({
  rootMargin: '0% 0% -2% 0%',
  threshold: 0,
  initialDelay: 100,

  runs: {
    booking: {
      threshold: 0.0,
      repeated: true,
      callback: (element, repeated) => {
        if (repeated) {
          const bookingBar = document.querySelector('.booking-bar')
          if (bookingBar) {
            bookingBar.classList.add('is-transitioning')
            setTimeout(() => {
              bookingBar.classList.remove('is-transitioning')
            }, 300)
          }
          document.body.classList.remove('booking-bar-active')
          return
        }

        gsap.to(element, {
          opacity: 1,
          duration: 0.35,
        })
      },
      onExit: (_element, _repeated, meta) => {
        if (meta.direction === 'top') {
          const bookingBar = document.querySelector('.booking-bar')
          if (bookingBar) {
            bookingBar.classList.add('is-transitioning')
            setTimeout(() => {
              bookingBar.classList.remove('is-transitioning')
            }, 300)
          }
          document.body.classList.add('booking-bar-active')
        }
      },
    },

    bookvisit: {
      threshold: 0.0,
      repeated: true,
      onReady: async element => {
        element.setAttribute('data-ready-time', Date.now())

        // Load BookVisit script if not already loaded
        if (!window.BV) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.src = '//reservations.bookvisit.com/widget?type=accommodation'
            script.onload = resolve
            script.onerror = reject
            document.head.appendChild(script)
          })
        }

        const language = app.language === 'no' ? 'nb' : 'en-GB'

        // Get config from data attributes
        const config = {
          channelId: element.dataset.channelId || '004704a9-4399-48b7-991e-994bb578b97e',
          widgetLayout: 'StandardWide',
          WidgetContainerId: 'widgetContainer',
          promoCode: 'false',
          corpCode: 'false',
          agentCode: 'false',
          openOption: 'newwindow',
          loginLink: 'false',
          currency: 'NOK',
          languageCulture: language,
          accommodationTypePicker: 'false',
          type: 'single_property',
        }

        const customStyles = `
          [role="application"],
          .bv-widget-container,
          #root,
          * {
            font-family: "Ytri Flare", sans-serif !important;
          }
            
          #bv-widget-container {
            font-size: 18px !important;
          }

          @media (min-width: 0) and (max-width: 767px) {
            #bv-widget-container [class*="gap-[10px]"] {
              gap: 20px !important;
            }
          }

          .text-bv_smallFontSize {
            font-size: 18px !important;
          }

          * {
            border-color: black;
          }

          svg:not(:root).svg-inline--fa, svg:not(:host).svg-inline--fa {
            overflow: visible;
            box-sizing: content-box;
            color: black;
          }

          .text-white {
            color: black;
            font-weight: 350;
          }

          @media (min-width: 0) and (max-width: 767px) {
            #calendar-dialog {
              --bv-bodyColor: 0, 0, 0 !important;
            }

            [role="application"]:not(#bv-widget-container) {
              --bv-bodyColor: 0, 0, 0 !important;
            }

            h2 {
              color: black !important;
            }

            #calendar-dialog h2 {
              color: black !important;
            }
          }

          @media (min-width: 768px) {
            [class*="bg-black/[0.15]"] {
              background-color: rgba(197, 220, 255, 0.4);
            }

            [class*="bg-black/[0.25]"]:hover {
              background-color: rgba(197, 220, 255, 0.1);
            }
          }

          [data-icon="chevron-down"] {
            height: 0.8em !important;
            width: 0.8em !important;
            min-width: 0.8em !important;
          }

          [data-icon="calendar"] {
            margin-top: 3px;
          }

          [data-icon="user"] {
            margin-top: 3px;
          }

          .bv_inputBorder {
            clip-path: polygon(
              5px 0,
              calc(100% - 5px) 0,
              100% 5px,
              100% calc(100% - 5px),
              calc(100% - 5px) 100%,
              5px 100%,
              0 calc(100% - 5px),
              0 5px
            );
          }
        `

        // Initialize widget
        await window.BV.SPWidget({ ...config })

        // Wait for shadow roots to be created
        await new Promise(resolve => setTimeout(resolve, 1000))

        const container = document.getElementById('widgetContainer')
        const shadowRoot = container?.shadowRoot

        const portalContainer = document.getElementById(
          'bv-widget-portal-container-widgetContainer'
        )
        const portalShadowRoot = portalContainer?.shadowRoot

        // Inject into main shadow root
        if (shadowRoot) {
          const style = document.createElement('style')
          style.textContent = customStyles
          shadowRoot.appendChild(style)

          const rootEl = shadowRoot.getElementById('root')
          if (rootEl) {
            rootEl.style.setProperty('--bv-bodyFontFamily', '"Ytri Flare", sans-serif')
            rootEl.style.setProperty('--bv-bodyColor', '197, 220, 255')
            rootEl.style.setProperty('--bv-inputBorderWidth', '0px')
          }
        }

        // Function to inject styles into portal
        const injectPortalStyles = portalRoot => {
          if (portalRoot.querySelector('style[data-custom-injected]')) {
            return
          }

          const portalStyle = document.createElement('style')
          portalStyle.setAttribute('data-custom-injected', 'true')
          portalStyle.textContent = customStyles
          portalRoot.appendChild(portalStyle)

          const portalRootEl = portalRoot.getElementById('root')
          if (portalRootEl) {
            portalRootEl.style.setProperty('--bv-bodyFontFamily', '"Ytri Flare", sans-serif')
            portalRootEl.style.setProperty('--bv-bodyColor', '197, 220, 255')
            portalRootEl.style.setProperty('--bv-inputBorderWidth', '0px')
            portalRootEl.style.setProperty('--bv-dividerBorderColor', '197, 220, 255')
          }
        }

        // Inject into portal shadow root if it exists
        if (portalShadowRoot) {
          injectPortalStyles(portalShadowRoot)
        }

        // Watch for portal container to be created
        const portalObserver = new MutationObserver(() => {
          const portal = document.getElementById('bv-widget-portal-container-widgetContainer')
          if (portal?.shadowRoot) {
            injectPortalStyles(portal.shadowRoot)
          }
        })

        portalObserver.observe(document.body, {
          childList: true,
          subtree: true,
        })

        // fade in right away, since we want the patterns to be visible
        // Fade in when ready
        gsap.to(element, {
          opacity: 1,
          duration: 0.35,
        })

        Dom.addClass(element, 'booking-ready')
      },
      callback: async (_element, repeated) => {
        if (repeated) {
          const bookingBar = document.querySelector('.booking-bar')
          if (bookingBar) {
            bookingBar.classList.add('is-transitioning')
            setTimeout(() => {
              bookingBar.classList.remove('is-transitioning')
            }, 300)
          }
          document.body.classList.remove('booking-bar-active')
          return
        }
      },
      onExit: (_element, _repeated, meta) => {
        if (meta.direction === 'top') {
          const bookingBar = document.querySelector('.booking-bar')
          if (bookingBar) {
            bookingBar.classList.add('is-transitioning')
            setTimeout(() => {
              bookingBar.classList.remove('is-transitioning')
            }, 300)
          }
          document.body.classList.add('booking-bar-active')
        }
      },
    },

    loop: {
      threshold: 0.0,
      repeated: true,
      callback: (element, repeated) => {
        if (repeated) {
          element.$loop.play()
          return
        }
      },

      onExit: (element, _repeated) => {
        element.$loop.pause()
      },
    },
  },

  walks: {
    default: {
      interval: 0.2,
      duration: 0.35,
      transition: null,
    },

    r: {
      interval: 0.1,
      duration: 0.35,
      transition: null,
    },

    l: {
      interval: 0.08,
      duration: 0.35,
      transition: null,
    },

    u: {
      interval: 0.1,
      duration: 0.35,
      transition: null,
    },

    uFast: {
      interval: 0.03,
      duration: 0.35,
      alphaTween: true,
      transition: {
        from: {
          y: 30,
        },

        to: {
          y: 0,
          ease: 'power3.out',
        },
      },
    },

    bigUp: {
      interval: 0.25,
      duration: 0.4,
      transition: null,
    },

    slideInLeft: {
      interval: 0.2,
      duration: 1,
      alphaTween: false,
      transition: {
        from: {
          opacity: 1,
          xPercent: -100,
        },

        to: {
          opacity: 1,
          xPercent: 0,
          ease: 'power3.out',
        },
      },
    },

    slideInRight: {
      interval: 0.2,
      duration: 1,
      alphaTween: false,
      transition: {
        from: {
          opacity: 1,
          xPercent: 100,
        },

        to: {
          opacity: 1,
          xPercent: 0,
          ease: 'power3.out',
        },
      },
    },
  },
})
